/* Direct pack selection. The native product form owns checkout only when no pack is selected. */
(() => {
  if (customElements.get('para-complete-look')) return;

  const instances = new Set();
  let activeOperation = null;
  let modernEventsPromise;

  function modernEvents() {
    if (!modernEventsPromise) {
      const hasStandardEvents = [...document.querySelectorAll('script[type="importmap"]')].some((script) => {
        try {
          return Boolean(JSON.parse(script.textContent).imports?.['@shopify/events']);
        } catch {
          return false;
        }
      });
      if (hasStandardEvents) {
        let timer;
        modernEventsPromise = Promise.race([
          import('@shopify/events').catch(() => null),
          new Promise((resolve) => { timer = setTimeout(() => resolve(null), 6000); }),
        ]).finally(() => clearTimeout(timer));
      } else {
        modernEventsPromise = Promise.resolve(null);
      }
    }
    return modernEventsPromise;
  }

  function refreshInstances() {
    for (const instance of instances) instance.renderControls();
  }

  async function fetchJSON(url, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        signal: controller.signal,
        headers: { Accept: 'application/json', ...options.headers },
      });
      const data = await response.json();
      return { response, data };
    } finally {
      clearTimeout(timer);
    }
  }

  function backendMessage(data) {
    if (typeof data?.description === 'string') return data.description;
    if (typeof data?.errors === 'string') return data.errors;
    if (typeof data?.message === 'string') return data.message;
    if (data?.errors && typeof data.errors === 'object') {
      return Object.values(data.errors).flat().filter((value) => typeof value === 'string').join(' ');
    }
    return '';
  }

  class ParaCompleteLook extends HTMLElement {
    constructor() {
      super();
      this.lifecycle = 0;
      this.cardDefaults = new WeakMap();
    }

    connectedCallback() {
      this.lifecycle += 1;
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.addEventListener('change', (event) => {
        const card = event.target.closest('[data-look-card]');
        if (!card || card.closest('para-complete-look') !== this) return;
        if (event.target.matches('[data-look-variant]')) this.renderCard(card);
        if (event.target.matches('[data-look-check]') && event.target.checked) {
          // One alternative per product form, even if two blocks offer packs.
          for (const instance of instances) {
            if (instance.getForm() !== this.getForm()) continue;
            for (const check of instance.querySelectorAll('[data-look-check]:checked')) {
              if (check !== event.target) check.checked = false;
            }
            instance.showStatus('');
          }
        } else this.showStatus('');
        refreshInstances();
      }, options);

      this.form = this.getForm();
      this.mainScope = this.form?.closest('.shopify-section');
      this.mainScope?.addEventListener('variant:update', () => {
        queueMicrotask(() => { if (this.isConnected) this.renderControls(); });
      }, options);
      for (const type of ['input', 'change']) this.mainScope?.addEventListener(type, (event) => {
        if (event.target.name === 'quantity') this.renderControls();
      }, options);
      instances.add(this);
      for (const card of this.cards()) this.renderCard(card);
      this.dataset.ready = 'true';
      this.renderControls();
      // Warm the theme's existing event module; absence means the older Fabric API.
      modernEvents();
    }

    disconnectedCallback() {
      this.lifecycle += 1;
      this.listeners?.abort();
      instances.delete(this);
      this.restoreNative(this.form);
      refreshInstances();
      // Let an in-flight write settle; aborting it cannot undo a Shopify addition.
    }

    money(cents) {
      const currency = this.dataset.currency || 'EUR';
      const key = JSON.stringify([this.dataset.locale, currency]);
      if (this.moneyFormatKey !== key) {
        this.moneyFormatter = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency });
        this.moneyFormatKey = key;
      }
      return this.moneyFormatter.format(cents / 100);
    }
    getForm() {
      const selector = `product-form-component[data-product-id="${this.dataset.productId}"] form`;
      return this.closest('.shopify-section')?.querySelector(selector)
        || document.querySelector(`main ${selector}`);
    }
    checkedCards() { return this.cards().filter(card => card.querySelector('[data-look-check]')?.checked); }
    restoreNative(form) {
      if (!form) return;
      form.removeAttribute('data-look-selected');
      form.querySelectorAll('[data-pack-submit]').forEach((button) => button.remove());
      for (const sticky of this.stickyBars(form)) sticky.removeAttribute('data-pack-selected');
      for (const price of form.closest('.shopify-section')?.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`) || []) {
        price.removeAttribute('data-look-selected');
        price.querySelector('[data-look-main-total]')?.remove();
      }
    }

    stickyBars(form) {
      return form.closest('.shopify-section')?.querySelectorAll(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`) || [];
    }

    selection(card) {
      const option = this.selectedOption(card);
      const quantity = Number(this.getForm()?.elements.namedItem('quantity')?.value ?? 1);
      let error = '';
      if (!this.isAvailable(option)) error = this.dataset.messageSelect;
      else if (!Number.isSafeInteger(quantity) || quantity < 1 || (option.dataset.max && quantity > Number(option.dataset.max))) {
        error = this.dataset.messageQuantity;
      }
      return { card, option, quantity, error };
    }

    fillTotal(element, { option, quantity }) {
      const price = Number(option.dataset.cents);
      const compare = Number(option.dataset.compareCents);
      element.replaceChildren(document.createTextNode(`${this.dataset.messagePackTotal} ${this.money(price * quantity)}`));
      if (compare > price) {
        const old = document.createElement('s');
        old.textContent = this.money(compare * quantity);
        element.append(' ', old, document.createTextNode(` — ${this.dataset.messageSave} ${this.money((compare - price) * quantity)}`));
      }
    }

    renderPack() {
      const form = this.getForm();
      if (this.form && this.form !== form) this.restoreNative(this.form);
      this.form = form;
      const card = this.checkedCards()[0];
      const state = card && this.selection(card);
      const total = this.querySelector('[data-look-total]');
      if (total) {
        total.hidden = !state;
        if (state && !state.error) this.fillTotal(total, state);
        else total.textContent = state?.error || '';
      }
      if (!form) return;
      if (!state) {
        if (![...instances].some((other) => other !== this && other.getForm() === form && other.checkedCards().length)) this.restoreNative(form);
        return;
      }
      form.setAttribute('data-look-selected', '');
      for (const price of form.closest('.shopify-section')?.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`) || []) {
        let extra = price.querySelector('[data-look-main-total]');
        if (!extra) {
          extra = document.createElement('p');
          extra.dataset.lookMainTotal = '';
          extra.className = 'para-look__bundle-total';
          price.append(extra);
        }
        if (!state.error) this.fillTotal(extra, state);
        else extra.textContent = state.error;
        price.setAttribute('data-look-selected', '');
      }
      // Keep the original form button and its live inventory state intact. A
      // pack buys a separate SKU and shouldn't validate the main product's fields.
      const label = activeOperation?.element === this ? this.dataset.messageAdding
        : this.dataset.messageAddPack + (state.error ? '' : ' — ' + this.money(Number(state.option.dataset.cents) * state.quantity));
      for (const native of form.querySelectorAll('add-to-cart-component button[type="submit"]:not([data-pack-submit])')) {
        let button = native.parentElement.querySelector('[data-pack-submit]');
        if (!button) {
          button = document.createElement('button');
          button.type = 'submit';
          button.formNoValidate = true;
          button.dataset.packSubmit = '';
          button.className = native.className;
          native.before(button);
        }
        button.disabled = Boolean(activeOperation);
        button.textContent = label;
      }
      for (const sticky of this.stickyBars(form)) {
        const button = sticky.querySelector('[data-sticky-pack-submit]');
        if (!button) continue;
        sticky.setAttribute('data-pack-selected', '');
        button.disabled = Boolean(activeOperation);
        button.textContent = label;
        const name = sticky.querySelector('[data-sticky-pack-name]');
        if (name) {
          const title = card.querySelector('.para-look__title')?.textContent.trim() || '';
          const variantTitle = state.option?.value && !card.querySelector('[data-look-variant]')?.hidden
            ? state.option.textContent.trim() : '';
          name.textContent = [title, variantTitle].filter(Boolean).join(' — ');
        }
      }
    }

    addSelection(button) {
      const cards = this.checkedCards();
      if (activeOperation || cards.length !== 1) return;
      const state = this.selection(cards[0]);
      if (state.error) {
        this.showStatus(state.error);
        if (!this.isAvailable(state.option)) cards[0].querySelector('[data-look-variant]')?.focus();
        return;
      }
      this.addPack(cards[0], button, { id: state.option.value, quantity: state.quantity });
    }

    cards() {
      return [...this.querySelectorAll('[data-look-card]')].filter((card) => card.closest('para-complete-look') === this);
    }

    selectedOption(card) {
      return card.querySelector('[data-look-variant]')?.selectedOptions[0];
    }

    isAvailable(option) {
      return Boolean(option && /^\d+$/.test(option.value) && option.dataset.available === 'true' && !option.disabled);
    }

    renderCard(card) {
      const option = this.selectedOption(card);
      const current = card.querySelector('[data-look-current]');
      const compare = card.querySelector('[data-look-compare]');
      const picture = card.querySelector('[data-look-image]');
      const links = [...card.querySelectorAll('[data-look-link]')];
      if (!this.cardDefaults.has(card)) {
        this.cardDefaults.set(card, {
          price: current?.textContent,
          compare: compare?.textContent,
          src: picture?.getAttribute('src'),
          srcset: picture?.getAttribute('srcset'),
          links: links.map((link) => [link, link.getAttribute('href')]),
        });
      }
      const defaults = this.cardDefaults.get(card);
      const selected = Boolean(option?.value);
      const previousPrice = (selected ? option.dataset.compare : defaults.compare) || '';
      if (current) current.textContent = (selected ? option.dataset.price : defaults.price) || '';
      if (compare) {
        compare.textContent = previousPrice;
        card.querySelector('[data-look-was]').hidden = !previousPrice;
        card.querySelector('[data-look-sale-label]').hidden = !previousPrice;
      }
      if (picture) {
        const src = option?.dataset.image || defaults.src;
        if (src && picture.getAttribute('src') !== src) picture.setAttribute('src', src);
        if (option?.dataset.image) picture.removeAttribute('srcset');
        else if (defaults.srcset) picture.setAttribute('srcset', defaults.srcset);
      }
      for (const [link, href] of defaults.links) {
        if (option?.dataset.url || href) link.setAttribute('href', option?.dataset.url || href);
      }
      const preorder = card.querySelector('[data-look-preorder]');
      if (preorder) preorder.hidden = option?.dataset.preorder !== 'true';
    }

    renderControls() {
      const busy = Boolean(activeOperation);
      this.setAttribute('aria-busy', String(activeOperation?.element === this));
      const form = this.getForm();
      for (const card of this.cards()) {
        const select = card.querySelector('[data-look-variant]');
        const checkbox = card.querySelector('[data-look-check]');
        const available = select && [...select.options].some((candidate) => this.isAvailable(candidate));
        if (select) select.disabled = busy || !available;
        if (checkbox) {
          checkbox.disabled = busy || !available || !form;
          if (!available || !form) checkbox.checked = false;
        }
      }
      this.renderPack();
    }

    showStatus(message, showCart = false) {
      const status = this.querySelector('[data-look-status]');
      if (status) {
        status.textContent = message || '';
        status.hidden = !message;
      }
      const link = this.querySelector('[data-look-cart-link]');
      if (link) link.hidden = !showCart;
    }

    async readCart() {
      const { response, data } = await fetchJSON(this.dataset.cartJsonUrl, { cache: 'no-store' });
      if (!response.ok || !Number.isInteger(data?.item_count) || data.item_count < 0 || !Array.isArray(data.items)) {
        throw new Error('Invalid cart response');
      }
      return data;
    }

    async publishCart(cart, result, items, didError, lifecycle) {
      const events = await modernEvents();
      if (!this.isConnected || this.lifecycle !== lifecycle) return;
      const detail = {
        source: 'para-complete-look',
        sourceId: this.id,
        itemCount: cart.item_count,
        items: cart.items,
        sections: result?.sections,
        didError,
        openCart: false,
      };
      let event;
      if (events?.CartLinesUpdateEvent) {
        event = new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: Promise.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail,
          }),
        });
      } else {
        event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: this.id, data: detail },
        });
      }

      // Fabric's auto-open also runs on error refreshes. Suppress only our own
      // synchronous event, then restore immediately so other theme actions retain it.
      const autoDrawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
      const previousValues = autoDrawers.map((drawer) => [drawer, drawer.getAttribute('auto-open')]);
      try {
        for (const drawer of autoDrawers) drawer.removeAttribute('auto-open');
        this.dispatchEvent(event);
      } finally {
        for (const [drawer, value] of previousValues) drawer.setAttribute('auto-open', value);
      }

      if (didError || this.dataset.cartBehavior !== 'drawer') return;
      // Allow the modern promise subscribers to start rendering the returned sections.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (!this.isConnected || this.lifecycle !== lifecycle) return;
      const drawer = document.querySelector('cart-drawer-component');
      const controller = drawer?.closest('theme-drawer') || drawer;
      if (typeof controller?.open === 'function') controller.open();
      else if (typeof controller?.showDialog === 'function') controller.showDialog();
    }

    async addPack(card, button, item) {
      if (activeOperation) return;
      const items = [item];
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((section) => section.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const body = { items, sections_url: window.location.pathname };
      if (sections.length) body.sections = sections;
      const lifecycle = this.lifecycle;
      const operation = { element: this, form: this.getForm(), button };
      const mutationDetail = { source: 'para-complete-look', token: operation, openCart: false };
      const begin = new CustomEvent('para:cart-mutation:start', {
        bubbles: true, cancelable: true, detail: mutationDetail,
      });
      if (!this.dispatchEvent(begin)) {
        // A cart extension may already own the cart. Do not issue a competing write.
        document.dispatchEvent(new CustomEvent('para:cart-mutation:end', { detail: mutationDetail }));
        this.showStatus(this.dataset.messageBusy || this.dataset.messageError);
        return;
      }
      activeOperation = operation;
      refreshInstances();
      this.showStatus(this.dataset.messageAdding);

      let result = null;
      let confirmed = false;
      let failureMessage = '';
      try {
        try {
          const { response, data } = await fetchJSON(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }, 25000);
          result = data;
          confirmed = response.ok && !data?.errors && !(Number(data?.status) >= 400) && Array.isArray(data?.items)
            && data.items.length > 0;
          if (!confirmed) failureMessage = backendMessage(data);
        } catch {
          // A network timeout can happen after Shopify has accepted some/all items.
        }

        let cart;
        try {
          cart = await this.readCart();
        } catch {
          // A failed read must never turn a confirmed addition into a failed one.
        }
        if (!this.isConnected || this.lifecycle !== lifecycle) return;

        if (confirmed) {
          const checkbox = card.querySelector('[data-look-check]');
          if (checkbox) checkbox.checked = false;
          this.showStatus(this.dataset.messageAdded, true);
        } else {
          this.showStatus([failureMessage || this.dataset.messageError, this.dataset.messageUncertain].filter(Boolean).join(' '), true);
        }

        if (cart) {
          try {
            await this.publishCart(cart, result, items, !confirmed, lifecycle);
          } catch {
            // Keep the real mutation outcome and cart link if theme rendering fails.
          }
        }
        if (confirmed && this.isConnected && this.lifecycle === lifecycle && this.dataset.cartBehavior === 'cart') {
          window.location.assign(this.dataset.cartUrl);
        }
      } finally {
        if (activeOperation === operation) activeOperation = null;
        // Dispatch on document so a removed/re-rendered product block still releases the cart.
        document.dispatchEvent(new CustomEvent('para:cart-mutation:end', { detail: mutationDetail }));
        refreshInstances();
      }
    }
  }

  const controllers = form => [...instances].filter(instance => instance.getForm() === form && instance.checkedCards().length);
  window.ParaLookBundle = {
    handles: form => Boolean(form && (activeOperation?.form === form || controllers(form).length)),
    submit(form, button) {
      if (activeOperation?.form === form) return true;
      const matches = controllers(form);
      // A pack-only submitter must not buy the main product if its card was
      // replaced or deselected between activation and the submit event.
      if (!matches.length) return Boolean(button?.matches?.('[data-pack-submit], [data-sticky-pack-submit]'));
      // A conflicting stale selection must never fall through to a main product add.
      if (matches.length !== 1 || matches[0].checkedCards().length !== 1) {
        matches[0].showStatus(matches[0].dataset.messageSelect);
        return true;
      }
      matches[0].addSelection(button);
      return true;
    },
  };
  customElements.define('para-complete-look', ParaCompleteLook);
})();
